import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
  modalModes,
  useCropperDispatch,
  useCropperState,
  actions,
  stateKeys,
} from '../../model';
import { useValueRef } from '../../utils/valueRef';
import { getCroppedFile } from '../../utils/getCroppedFile';
import { formatZoom } from '../../utils/common';

const { cropperModalCancel, errorModalCancel, setErr, resetState, closeModal } =
  actions;

const StateContext = createContext();
const ActionsContext = createContext();

export const useCropperModalState = () => useContext(StateContext);
export const useCropperModalActions = () => useContext(ActionsContext);

const cancelFns = {
  [modalModes.initial]: (dispatch) => dispatch(closeModal()),
  [modalModes.cropper]: (dispatch) => dispatch(cropperModalCancel()),
  [modalModes.error]: (dispatch) => dispatch(errorModalCancel()),
};

const Provider = ({ children, onCropOk, confirmLoading, inForm }) => {
  const {
    [stateKeys.modalVisible]: visible,
    [stateKeys.modalMode]: mode,
    [stateKeys.file]: file,
    [stateKeys.fileSrc]: fileSrc,
    [stateKeys.zoom]: zoom,
    [stateKeys.croppedAreaPxls]: croppedArea,
  } = useCropperState();

  const dispatch = useCropperDispatch();

  const modeRef = useValueRef(mode);
  const onOkRef = useValueRef(onCropOk);
  const fileDataRef = useValueRef({ file, fileSrc, zoom, croppedArea });

  const onCancel = useCallback(() => {
    const cancel = cancelFns[modeRef.current];

    if (typeof cancel === 'function') {
      cancel(dispatch);
    }
  }, []);

  const onOk = useCallback(async () => {
    try {
      const fileData = fileDataRef.current;
      const { file, fileSrc, zoom, croppedArea } = fileData;

      const croppedFile = await getCroppedFile({
        src: fileSrc,
        fileName: file.name,
        pxlCrop: croppedArea,
      });

      const reset = () => dispatch(closeModal());

      onOkRef.current(
        { file, zoom: formatZoom(zoom), croppedArea, croppedFile },
        reset
      );
    } catch (err) {
      dispatch(setErr(['Не удалось обрезать изображение']));
    }
  }, []);

  const afterClose = useCallback(() => dispatch(resetState()), []);

  const state = useMemo(
    () => ({ visible, mode, confirmLoading, inForm }),
    [visible, mode, confirmLoading, inForm]
  );
  const actions = useMemo(
    () => ({ onCancel, onOk, afterClose }),
    [onCancel, onOk, afterClose]
  );

  return (
    <StateContext.Provider value={state}>
      <ActionsContext.Provider value={actions}>
        {children}
      </ActionsContext.Provider>
    </StateContext.Provider>
  );
};

export default Provider;
